import { useEffect } from "react";

import { FaClone, FaMinus, FaSquare, FaXmark } from "react-icons/fa6";

import { useTitleBar } from "./hooks/useTitleBar";

import LogoImage from "../../assets/images/logos/logo-right-text.png";

import { MenuBar } from "./MenuBar";

import Profile from "../../screens/Dashboard/SubComponents/Profile/Profile";

import PlanBadge from "./PlanBadge";
import StudioNotifications from "@/src/components/StudioNotifications/StudioNotifications";

import AudioConfirmModal from "../../components/AudioConfirmModal/AudioConfirmModal";

import type {
  CustomTitleBarProps,
  CustomButtonConfig,
} from "./CustomTitleBar.types";

import {
  useBroadcast,
  useRecording,
  useAuthConnection,
  useUserType,
} from "./CustomTitleBar.hooks";

import { useLocation } from "react-router-dom";

import { BroadcastButton } from "./BroadcastButton";

import { RecordButton } from "./RecordButton";

import { BroadcastHealth } from "../BroadcastHealth/BroadcastHealth";

import "./CustomTitleBar.css";

const EMPTY_BUTTONS: CustomButtonConfig[] = [];

export const CustomTitleBar = ({
  height = 35,

  customButtons = EMPTY_BUTTONS,

  visible = true,

  simpleMode = false,
}: CustomTitleBarProps) => {
  const location = useLocation();

  const {
    shouldRender,

    isMac,

    isMaximized,

    handleMinimize,

    handleToggleMaximize,

    handleClose,
  } = useTitleBar({ visible, height });

  // En mac los semáforos nativos (traffic lights) viven a la izquierda

  // (trafficLightPosition {x:20,y:15}, ver optimizedWindow) → padding para que

  // el logo/menú no queden debajo. En win/linux el padding normal.

  const leftPadClass = isMac ? "pl-[80px]" : "pl-4";

  const { activeAccount, isAuthenticated, loading } = useAuthConnection();

  const { isLocalRecording } = useUserType();

  const broadcastHook = useBroadcast();

  const recordingHook = useRecording();

  const {
    handleStart,

    handleStop,

    showModal,

    availableMicrophones,

    handleContinueWithoutAudio,

    handleAudioCancel,
  } = isLocalRecording ? recordingHook : broadcastHook;

  useEffect(() => {
    const electronAPI = window.electronAPI;

    if (!electronAPI) return;

    const handleTrayAction = (data?: unknown) => {
      if (data && typeof data === "object" && "action" in data) {
        const { action } = data as { action: "start" | "stop"; type: string };

        if (action === "start") {
          handleStart();
        } else if (action === "stop") {
          handleStop();
        }
      }
    };

    electronAPI.on?.("tray-action", handleTrayAction);

    return () => {
      electronAPI.removeListener("tray-action");
    };
  }, [handleStart, handleStop]);

  if (!shouldRender) {
    return visible ? (
      <div style={{ height: `${height}px`, width: "100%", flexShrink: 0 }} />
    ) : null;
  }

  const leftButtons = customButtons.filter((btn) => btn.position === "left");

  const rightButtons = customButtons.filter((btn) => btn.position === "right");

  const separatorHeight = Math.round(height * 0.5);

  if (simpleMode) {
    return (
      <div
        className="titlebar titlebar-glass z-[10000] flex items-center justify-between select-none text-foreground"
        style={{ height: `${height}px` }}
      >
        <div className={`flex items-center ${leftPadClass}`}>
          <img
            src={LogoImage}
            alt="Eriol"
            className="h-5 mr-2 object-contain shrink-0 self-center opacity-90"
          />
        </div>

        <div className="flex items-center">
          {isAuthenticated && (
            <>
              <div className="titlebar-nodrag flex items-center px-3">
                <Profile account={activeAccount} showSettings={false} />
              </div>
              {!isMac && (
                <div
                  className="titlebar-separator"
                  style={{ height: `${separatorHeight}px` }}
                />
              )}
            </>
          )}

          {/* Controles de ventana: solo win/linux. En mac = semáforos nativos. */}
          {!isMac && (
            <>
              <button
                type="button"
                className="titlebar-winbtn"
                style={{ height: `${height}px` }}
                title="Minimize"
                onClick={handleMinimize}
              >
                <FaMinus size={10} />
              </button>

              <button
                type="button"
                className="titlebar-winbtn"
                style={{ height: `${height}px` }}
                title={isMaximized ? "Restore" : "Maximize"}
                onClick={handleToggleMaximize}
              >
                {isMaximized ? <FaClone size={10} /> : <FaSquare size={10} />}
              </button>

              <button
                type="button"
                className="titlebar-winbtn titlebar-close"
                style={{ height: `${height}px` }}
                title="Close"
                onClick={handleClose}
              >
                <FaXmark size={12} />
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        className="titlebar titlebar-glass z-[10000] flex items-center justify-between select-none text-foreground"
        style={{ height: `${height}px` }}
      >
        <div className={`flex items-center ${leftPadClass}`}>
          <img
            src={LogoImage}
            alt="Eriol"
            className="h-5 mr-2 object-contain shrink-0 self-center opacity-90"
          />

          <MenuBar isAuthenticated={!!isAuthenticated} loading={loading} />

          {leftButtons.map((button) => (
            <button
              type="button"
              key={button.id}
              className="titlebar-action"
              title={button.title}
              onClick={button.onClick}
            >
              {button.text}
            </button>
          ))}
        </div>

        <div className="titlebar flex-1 flex items-center justify-center">
          {location.pathname === "/dashboard" && (
            <>
              {!isLocalRecording ? (
                <>
                  <BroadcastButton
                    height={height}
                    onLive={broadcastHook.onLive}
                    isLoading={broadcastHook.isLoading}
                    disabled={broadcastHook.disabled}
                    showAssembler={broadcastHook.showAssembler}
                    haveOutputStream={broadcastHook.haveOutputStream}
                    onStart={broadcastHook.handleStart}
                    onStop={broadcastHook.handleStop}
                  />

                  {/* Salud del broadcast (fps / Mbps / drops) mientras está al aire. */}
                  {broadcastHook.onLive && <BroadcastHealth />}
                </>
              ) : (
                <RecordButton
                  height={height}
                  onLive={recordingHook.onLive}
                  isLoading={recordingHook.isLoading}
                  disabled={recordingHook.showAssembler}
                  showAssembler={recordingHook.showAssembler}
                  onStart={recordingHook.handleStart}
                  onStop={recordingHook.handleStop}
                />
              )}
            </>
          )}
        </div>

        <div className="flex items-center">
          {rightButtons.map((button) => (
            <button
              type="button"
              key={button.id}
              className="titlebar-action"
              title={button.title}
              onClick={button.onClick}
            >
              {button.text}
            </button>
          ))}

          {isAuthenticated && (
            <>
              <PlanBadge />
              <StudioNotifications />

              <div className="titlebar-nodrag flex items-center px-3">
                <Profile account={activeAccount} />
              </div>

              {!isMac && (
                <div
                  className="titlebar-separator"
                  style={{ height: `${separatorHeight}px` }}
                />
              )}
            </>
          )}

          {/* Controles de ventana: solo win/linux. En mac = semáforos nativos. */}
          {!isMac && (
            <>
              <button
                type="button"
                className="titlebar-winbtn"
                style={{ height: `${height}px` }}
                title="Minimize"
                onClick={handleMinimize}
              >
                <FaMinus size={10} />
              </button>

              <button
                type="button"
                className="titlebar-winbtn"
                style={{ height: `${height}px` }}
                title={isMaximized ? "Restore" : "Maximize"}
                onClick={handleToggleMaximize}
              >
                {isMaximized ? <FaClone size={10} /> : <FaSquare size={10} />}
              </button>

              <button
                type="button"
                className="titlebar-winbtn titlebar-close"
                style={{ height: `${height}px` }}
                title="Close"
                onClick={handleClose}
              >
                <FaXmark size={12} />
              </button>
            </>
          )}
        </div>
      </div>

      <AudioConfirmModal
        isOpen={showModal}
        availableMicrophones={availableMicrophones}
        onContinueWithoutAudio={handleContinueWithoutAudio}
        onClose={handleAudioCancel}
      />
    </>
  );
};
